import { useEffect, useState, type FormEvent } from "react";
import type { JobInput, NewTask } from "../data/api.ts";
import { OP_FIELDS, OP_HAS_FOLD, OP_HAS_PAGES, typesFor } from "../data/catalog.ts";
import { FOLD_TYPES, FORMATS, OPERATION_LABEL, type CatalogItem, type Job, type Machine, type Operation, type Task } from "../data/types.ts";
import { addDays, fmtDateTime, fromLocalInput, toLocalInput } from "../lib/time.ts";
import { Badge, ErrorBox, Field, Loading, attempt, navigate, useApp } from "../ui/kit.tsx";
import { SpecInput } from "./Settings.tsx";
import { Header } from "./Planning.tsx";
import { JOB_STATUS } from "./Jobs.tsx";
import { JobDocs, type PendingDoc } from "./JobDocs.tsx";

type Step = NewTask & { status?: Task["status"] };

const blankStep = (op: Operation = "pli"): Step => ({
  operation: op, fold_type: OP_HAS_FOLD(op) ? "simple" : null, format_in: op === "pli" || op === "rainage_pliage" ? "A3" : op === "pelliculage" ? "SRA3" : "A4",
  format_out: op === "pelliculage" ? "SRA3" : "A4", paper: "", grammage: null, pages: OP_HAS_PAGES(op) ? 16 : null, params: {}, packaging: null,
});

function relDays(iso: string): string {
  const d = new Date(iso), t = new Date(); t.setHours(0, 0, 0, 0);
  const d0 = new Date(d); d0.setHours(0, 0, 0, 0);
  const n = Math.round((d0.getTime() - t.getTime()) / 86_400_000);
  if (d < new Date()) return "déjà passée";
  return n === 0 ? "aujourd'hui" : n === 1 ? "demain" : `dans ${n} jours`;
}

function defaultDue(): string {
  const d = addDays(new Date(), 3); d.setHours(16, 0, 0, 0); return d.toISOString();
}

export function JobFormPage({ id, copyOf }: { id: string | null; copyOf: string | null }) {
  const { api, can } = useApp();
  const edit = can("jobs");
  const [machines, setMachines] = useState<Machine[]>([]);
  const [packs, setPacks] = useState<CatalogItem[]>([]);
  useEffect(() => { api.machines().then(setMachines).catch(() => {}); api.catalog("conditionnement").then(setPacks).catch(() => {}); }, [api]);
  const editing = id && !copyOf ? id : null;
  const [job, setJob] = useState<Job | null>(null);
  const [form, setForm] = useState<JobInput>({ of_number: "", client: "", title: "", quantity: 1000, due_at: defaultDue(), priority: "normal", sheets_ready_at: null, notes: "" });
  const [steps, setSteps] = useState<Step[]>([blankStep()]);
  const [loaded, setLoaded] = useState(!id);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [pendingDocs, setPendingDocs] = useState<PendingDoc[]>([]);

  useEffect(() => {
    if (!id) return;
    api.job(id).then((r) => {
      if (!r) { setErr("Ce job n'existe plus."); setLoaded(true); return; }
      const { job: j, tasks } = r;
      setForm({ of_number: copyOf ? "" : j.of_number, client: j.client, title: j.title, quantity: j.quantity,
        due_at: copyOf ? defaultDue() : j.due_at, priority: j.priority, sheets_ready_at: copyOf ? null : j.sheets_ready_at, notes: j.notes });
      setSteps(tasks.map((t) => ({
        id: copyOf ? undefined : t.id, operation: t.operation, fold_type: t.fold_type, format_in: t.format_in, format_out: t.format_out,
        paper: t.paper, grammage: t.grammage, pages: t.pages, params: t.params || {}, packaging: t.packaging, status: copyOf ? undefined : t.status,
      })));
      if (!copyOf) setJob(j);
      setLoaded(true);
    }).catch((e) => { setErr(e.message); setLoaded(true); });
  }, [api, id, copyOf]);

  if (!loaded) return <Loading />;
  if (err && !job && id && !copyOf) return <ErrorBox message={err} />;

  const set = <K extends keyof JobInput>(k: K, v: JobInput[K]) => setForm({ ...form, [k]: v });
  const setStep = (i: number, patch: Partial<Step>) => setSteps(steps.map((s, k) => (k === i ? { ...s, ...patch } : s)));
  const locked = (s: Step) => !!s.status && s.status !== "a_planifier" && s.status !== "planifie";

  const submit = async (e: FormEvent) => {
    e.preventDefault(); setErr("");
    if (!steps.length) { setErr("Ajoutez au moins une étape (pliage, encartage…)."); return; }
    if (!form.quantity || form.quantity < 1) { setErr("Indiquez la quantité."); return; }
    setBusy(true);
    const clean = steps.map(({ status: _s, ...s }) => s);
    const saved = await attempt(() => api.saveJob(editing, form, clean), editing ? "Job enregistré" : "Job créé : il attend dans « À planifier »");
    if (saved && pendingDocs.length) {
      for (const d of pendingDocs) await attempt(() => api.addDocument(saved.id, { name: d.name, kind: d.kind, thumb: d.thumb, pages: d.pages }));
      setPendingDocs([]);
    }
    setBusy(false);
    if (saved) navigate(editing ? "jobs" : "planning");
  };

  const status = job ? JOB_STATUS[job.status] : null;
  const fromMachines = [...new Set(machines.flatMap((m) => m.operations))];
  const addable = (Object.keys(OPERATION_LABEL) as Operation[]).filter((o) => !fromMachines.length ? o === "pli" || o === "encartage_piquage" : fromMachines.includes(o));
  const anyStarted = steps.some(locked);
  return (
    <div className="page page-form">
      <Header title={editing ? `Job ${form.of_number || ""}` : copyOf ? "Nouveau job (copie)" : "Nouveau job"}>
        {status && <Badge tone={status.tone}>{status.label}</Badge>}
        <span className="spacer" />
        {edit && editing && <a className="btn" href={`#/jobs/${editing}/copie`}>Dupliquer</a>}
      </Header>
      <form className="job-form" onSubmit={submit}>
        <div className="jf-main">
        {!edit && <div className="readonly">Lecture seule : votre rôle ne permet pas de modifier les jobs.</div>}
        <section className="card form-card">
        <h2 className="form-sec">Le job</h2>
        <fieldset className="bare" disabled={!edit}>
        <div className="grid-2">
          <Field label="N° d'OF" hint="Celui de votre logiciel de gestion"><input value={form.of_number} onChange={(e) => set("of_number", e.target.value)} placeholder="OF-2625" /></Field>
          <Field label="Client"><input value={form.client} onChange={(e) => set("client", e.target.value)} required /></Field>
          <Field label="Intitulé" wide><input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Dépliant 3 volets, catalogue 24 p.…" required /></Field>
          <Field label="Quantité (exemplaires)"><input type="number" min={1} value={form.quantity} onChange={(e) => set("quantity", Number(e.target.value))} required /></Field>
          <Field label="Priorité">
            <select value={form.priority} onChange={(e) => set("priority", e.target.value as JobInput["priority"])}>
              <option value="normal">Normale</option><option value="urgent">Urgente</option>
            </select>
          </Field>
          <Field label="Échéance de livraison"><input type="datetime-local" value={toLocalInput(form.due_at)} onChange={(e) => set("due_at", fromLocalInput(e.target.value) || form.due_at)} required /></Field>
          <Field label="Feuilles disponibles le" hint="Sortie d'impression prévue. Vide = déjà disponibles."><input type="datetime-local" value={toLocalInput(form.sheets_ready_at)} onChange={(e) => set("sheets_ready_at", fromLocalInput(e.target.value))} /></Field>
          <Field label="Consignes pour l'atelier" wide hint="Visibles en grand sur la tablette du poste."><textarea rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Sens d'ouverture, contrôles, colisage…" /></Field>
        </div>

        </fieldset>
        </section>
        <section className="card form-card">
        <h2 className="form-sec">Étapes de façonnage</h2>
        <p className="muted small form-sec-intro">Chaque étape devient une tâche à planifier sur la bonne machine, dans l'ordre. Les réglages marqués d'un point orange demandent un recalage quand ils changent.</p>
        <div className="steps-edit">
          {steps.map((s, i) => {
            const packOptions = packs.filter((c) => typesFor(s.operation).includes(c.scope as never) || c.scope === "").map((c) => c.label);
            const lock = locked(s) || !edit;
            return (
              <div key={i} className={`step-card ${locked(s) ? "locked" : ""}`}>
                <div className="step-num">{i + 1}</div>
                <div className="step-body">
                  <div className="grid-4">
                    <Field label="Opération">
                      <select disabled={lock} value={s.operation} onChange={(e) => { const op = e.target.value as Operation; setStep(i, { ...blankStep(op), id: s.id, status: s.status }); }}>
                        {(Object.keys(OPERATION_LABEL) as Operation[]).map((o) => <option key={o} value={o}>{OPERATION_LABEL[o]}</option>)}
                      </select>
                    </Field>
                    {OP_HAS_FOLD(s.operation) && (
                      <Field label="Type de pli">
                        <select disabled={lock} value={s.fold_type || ""} onChange={(e) => setStep(i, { fold_type: e.target.value })}>
                          {FOLD_TYPES.map((f) => <option key={f}>{f}</option>)}
                        </select>
                      </Field>
                    )}
                    {OP_HAS_PAGES(s.operation) && (
                      <Field label="Nombre de pages"><input disabled={lock} type="number" min={4} step={4} value={s.pages ?? ""} onChange={(e) => setStep(i, { pages: e.target.value ? Number(e.target.value) : null })} /></Field>
                    )}
                    <Field label="Format entrée">
                      <input disabled={lock} list="formats" value={s.format_in} onChange={(e) => setStep(i, { format_in: e.target.value })} required />
                    </Field>
                    <Field label="Format sortie">
                      <input disabled={lock} list="formats" value={s.format_out} onChange={(e) => setStep(i, { format_out: e.target.value })} required />
                    </Field>
                    <Field label="Papier"><input disabled={lock} value={s.paper} onChange={(e) => setStep(i, { paper: e.target.value })} placeholder="Couché mat" /></Field>
                    <Field label="Grammage (g)"><input disabled={lock} type="number" min={40} value={s.grammage ?? ""} onChange={(e) => setStep(i, { grammage: e.target.value ? Number(e.target.value) : null })} /></Field>
                  </div>
                  <fieldset className="step-params" disabled={lock}>
                    <div className="grid-4">
                      {OP_FIELDS[s.operation].filter((f) => f.kind !== "bool").map((f) => (
                        <div key={f.key} className={f.setup ? "is-setup" : ""}>
                          <SpecInput f={f} value={s.params?.[f.key]} onChange={(v) => setStep(i, { params: { ...s.params, [f.key]: v } })} />
                        </div>
                      ))}
                      <Field label="Conditionnement" hint={packOptions.length ? undefined : "Ajoutez vos conditionnements dans Paramètres"}>
                        <select value={s.packaging || ""} onChange={(e) => setStep(i, { packaging: e.target.value || null })}>
                          <option value="">—</option>
                          {[...new Set([...packOptions, ...(s.packaging ? [s.packaging] : [])])].map((x) => <option key={x} value={x}>{x}</option>)}
                        </select>
                      </Field>
                    </div>
                    {OP_FIELDS[s.operation].some((f) => f.kind === "bool") && (
                      <div className="checks">
                        {OP_FIELDS[s.operation].filter((f) => f.kind === "bool").map((f) => (
                          <span key={f.key} className={f.setup ? "is-setup" : ""}><SpecInput f={f} value={s.params?.[f.key]} onChange={(v) => setStep(i, { params: { ...s.params, [f.key]: v } })} /></span>
                        ))}
                      </div>
                    )}
                  </fieldset>
                </div>
                {locked(s) ? <span className="step-lock">Démarrée à l'atelier</span> : edit && steps.length > 1 && (
                  <button type="button" className="icon-btn" aria-label="Supprimer l'étape" onClick={() => setSteps(steps.filter((_, k) => k !== i))}>✕</button>
                )}
              </div>
            );
          })}
          <datalist id="formats">{FORMATS.map((f) => <option key={f} value={f} />)}</datalist>
        </div>
        {edit && (
          <div className="add-steps">
            <span className="muted small">Ajouter une étape</span>
            {addable.map((op) => <button key={op} type="button" className="btn btn-small" onClick={() => setSteps([...steps, blankStep(op)])}>+ {OPERATION_LABEL[op]}</button>)}
          </div>
        )}
        </section>
        <JobDocs jobId={editing} edit={edit} pending={pendingDocs} setPending={setPendingDocs} />
        </div>

        <aside className="jf-side">
        <div className="card jf-summary">
          <h2>Résumé</h2>
          <div className="jf-id">
            <span className="jf-of">{form.of_number || "OF sans numéro"}</span>
            <b>{form.client || "Client à renseigner"}</b>
            <span className="muted">{form.title || "Intitulé"}</span>
          </div>
          <dl className="jf-facts">
            <dt>Quantité</dt><dd>{form.quantity ? `${form.quantity.toLocaleString("fr-FR")} ex.` : "—"}</dd>
            <dt>Échéance</dt><dd>{fmtDateTime(form.due_at)}<small>{relDays(form.due_at)}</small>{form.priority === "urgent" && <Badge tone="red">Urgent</Badge>}</dd>
            {form.sheets_ready_at && <><dt>Feuilles</dt><dd>{fmtDateTime(form.sheets_ready_at)}</dd></>}
          </dl>
          <ol className="jf-steps">
            {steps.map((s, i) => {
              const ms = machines.filter((m) => m.active && m.operations.includes(s.operation));
              return (
                <li key={i}>
                  <b>{OPERATION_LABEL[s.operation]}{s.fold_type ? ` ${s.fold_type}` : ""}</b>
                  <span>{s.format_in || "?"} → {s.format_out || "?"}{s.pages ? ` · ${s.pages} p.` : ""}</span>
                  <small className={ms.length ? "" : "warn"}>{ms.length ? ms.map((m) => m.name).join(", ") : "Aucune machine ne fait cette opération"}</small>
                </li>
              );
            })}
          </ol>
          {err && <p className="form-error">{err}</p>}
          <div className="jf-actions">
            {edit && <button className="btn btn-primary btn-block" disabled={busy}>{editing ? "Enregistrer" : "Créer le job"}</button>}
            {!editing && edit && <p className="muted small">Le job attendra dans « À planifier ».</p>}
            <a className="btn btn-block" href="#/jobs">Retour aux jobs</a>
          </div>
        </div>
        <div className="jf-more">
          {edit && editing && job && job.status !== "annule" && (
            <button type="button" className="btn btn-ghost danger" onClick={async () => {
              if (!confirm(anyStarted ? "Annuler ce job ? Il disparaîtra du planning." : "Supprimer définitivement ce job ?")) return;
              await attempt(() => (anyStarted ? api.cancelJob(job.id) : api.deleteJob(job.id)), anyStarted ? "Job annulé" : "Job supprimé");
              navigate("jobs");
            }}>{anyStarted ? "Annuler le job" : "Supprimer"}</button>
          )}
          {job && <p className="muted small">Créé {fmtDateTime(job.created_at)}, modifié {fmtDateTime(job.updated_at)}</p>}
        </div>
        </aside>
      </form>
    </div>
  );
}
